/**
 * A suggested name for a character the parent did not name.
 *
 * The default used to be the literal string `Sparkle` for everybody, so every
 * family that tapped straight through ended up with the same character name,
 * and the shared shelf filled with identical Sparkles. A name is the one part
 * of this a two-year-old actually repeats out loud — it should not be the same
 * word for every child in the product.
 *
 * Two properties matter more than the list itself:
 *
 *   NEUTRAL. We do not know the child's gender and have no business guessing
 *   the character's. Everything here reads as a name for an animal, a train or
 *   a digger without implying either.
 *
 *   STABLE. The suggestion is derived from the character, not drawn at random,
 *   so it does not change under the parent while they are looking at it — and
 *   re-entering the screen offers the same name rather than a new one.
 */
const NAMES = [
  'Pip', 'Bramble', 'Nutmeg', 'Poppy', 'Tumble', 'Willow', 'Biscuit', 'Juniper',
  'Marlow', 'Pebble', 'Clover', 'Rusty', 'Maple', 'Sprout', 'Hazel', 'Bracken',
  'Fern', 'Cinder', 'Perry', 'Wren', 'Tansy', 'Bodie', 'Sorrel', 'Fig',
  'Comet', 'Dash', 'Bumble', 'Scout', 'Robin', 'Ash', 'Tuppence', 'Mo',
];

/**
 * Pick one, deterministically, from whatever identifies this character.
 *
 * A plain sum of char codes would map anagrams together and cluster badly on
 * ids that share a prefix; this is the standard 32-bit string hash, which is
 * enough to spread UUIDs evenly across the list.
 */
export function suggestName(seed: string | undefined): string {
  if (!seed) return NAMES[0]!;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (Math.imul(31, hash) + seed.charCodeAt(i)) | 0;
  }
  return NAMES[Math.abs(hash) % NAMES.length]!;
}
